import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import ProductBox from '@components/storefront/ProductBox';
import { MEDIA, NAV_SEARCH_PRODUCTS_INITIAL_COUNT, NAV_SEARCH_PRODUCTS_SHOW_MORE_COUNT } from '@constants';
import { AlgoliaHit, NavStatus } from '@contracts';
import { ProductData, getShopProducts } from '@data/storefront';
import { useCountry } from '@hooks/useCountry';
import { applyCountrySpecificSorting, filterProducts } from '@utils/product';
import { NavContext } from './Context';

interface Props {
  setProductResultsCount: React.Dispatch<React.SetStateAction<number>>;
  shouldLoadMore: boolean;
  hits: AlgoliaHit[];
  query: string;
}

function NavSearchProductHits({ hits, query, shouldLoadMore, setProductResultsCount }: Props) {
  const { country } = useCountry();
  const shopProducts = getShopProducts();
  const { navDispatch } = useContext(NavContext);

  const products = useMemo(() => {
    const matchedProducts = [] as ProductData[];

    if (!hits?.length) {
      return matchedProducts;
    }

    const filteredProducts = filterProducts(country, shopProducts);

    if (!query) {
      return applyCountrySpecificSorting(country, filteredProducts);
    }

    hits.forEach((hit: AlgoliaHit) => {
      const relatedProductIndex = filteredProducts.findIndex((product) => {
        const finalProductCode = product?.product_code_override?.text || product?.product_code;

        return hit.id === finalProductCode;
      });

      if (relatedProductIndex !== -1) {
        matchedProducts.push(filteredProducts[relatedProductIndex]);
        filteredProducts.splice(relatedProductIndex, 1);
      }
    });

    return matchedProducts;
  }, [hits, shopProducts, query, country]);

  useEffect(() => {
    setProductResultsCount(products.length);
  }, [products]);

  const onHitClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navDispatch({ type: 'removeNavbarSelectedCategories' });
  };

  return (
    <Wrapper>
      <GridWrapper>
        {products.slice(0, shouldLoadMore ? NAV_SEARCH_PRODUCTS_SHOW_MORE_COUNT : NAV_SEARCH_PRODUCTS_INITIAL_COUNT).map((product) => (
          <HitWrapper key={product?.product_code_override?.text || product?.product_code}>
            <Hit>
              <ProductBox data={product} textColor={Colors.navyBlue} callback={onHitClick} />
            </Hit>
          </HitWrapper>
        ))}
      </GridWrapper>
    </Wrapper>
  );
}

export default NavSearchProductHits;
const Wrapper = styled.div`
  margin-bottom: -20px;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(250px, 1fr));
  gap: 20px;

  ${MEDIA.laptop} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);

    & .product-box-container {
      width: 100%;
    }
  }

  ${MEDIA.mobileM} {
    grid-template-columns: 1fr;
  }
`;

const HitWrapper = styled.div`
  margin-bottom: 20px;
`;

const Hit = styled.div`
  .product-image-wrap {
    border-radius: 10px;
  }
`;
